import './RevenueImpact.scss';
import { ReactElement, useCallback, useState } from 'react';
import { MdArrowForward } from 'react-icons/md';
import { Bar, BarChart, ResponsiveContainer } from 'recharts';
import { ChartXAxis, ChartYAxis } from '@Components/Charts/chart-presets';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { currencyFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { AreaChart } from '@Components/Charts/AreaChart/AreaChart';
import { Placement, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { Brand40, Brand50, Brand80, Neutral300, Neutral400, Neutral600 } from '@src/utils/colors';
import { dateToMddyy } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { DATE_GROUPING_OPTIONS_BRAND_EQUITY } from '../BrandEquityUtils';
import { percFormat } from '../BrandImpact/BrandImpactUtils';
import { useGroupedByDate } from '../useGroupedByDate';

interface TotalRevenueDecomposition {
  name: string;
  MediaDrivenBrandEquity: number;
  NonMediaDrivenBrandEquity: number;
  OtherFactors: number;
  MediaDrivenBrandEquityPercent: number;
  NonMediaDrivenBrandEquityPercent: number;
  OtherFactorsPercent: number;
}

interface WeeklyRevenueDecomposition {
  date: string;
  OtherFactors: number;
  total: number;
  MediaDrivenBrandEquity: number;
  NonMediaDrivenBrandEquity: number;
}

interface RevenueImpactTakeaways {
  name: string;
  MediaDrivenBrandEquity: number;
  BrandEquityRevenue: number;
  Total: number;
  BrandEquityRevenuePercent: number;
}

interface RevenueImpactProps {
  totalRevenueDecomposition: TotalRevenueDecomposition[];
  weeklyRevenueDecomposition: WeeklyRevenueDecomposition[];
  revenueImpactTakeaways: RevenueImpactTakeaways;
}

export const RevenueImpact = ({
  totalRevenueDecomposition,
  weeklyRevenueDecomposition,
  revenueImpactTakeaways
}: RevenueImpactProps): ReactElement => {
  const [
    weeklyRevenueDecompositionChartDateGrouping,
    setWeeklyRevenueDecompositionChartDateGrouping
  ] = useState<DateGroupingKey>('Week');
  const weeklyRevenueDecompositionByDate = useGroupedByDate(
    weeklyRevenueDecomposition,
    weeklyRevenueDecompositionChartDateGrouping
  );

  const excelDownloadTotalRevenueDecomposition = useCallback(() => {
    exportToExcel(totalRevenueDecomposition, 'weekly_brand_health');
  }, [totalRevenueDecomposition]);

  const excelDownloadWeeklyRevenueDecomposition = useCallback(() => {
    exportToExcel(weeklyRevenueDecomposition, 'weekly_spend');
  }, [weeklyRevenueDecomposition]);

  const excelDownloadModelOverview = useCallback(() => {
    exportToExcel([revenueImpactTakeaways], 'model_overview');
  }, [revenueImpactTakeaways]);

  const pngDownloadTotalRevenueDecomposition = useCallback(async () => {
    await downloadPNG('.revenue-impact-left .chart-container', 'total_revenue_decomposition');
  }, []);

  const pngDownloadWeeklyRevenueDecomposition = useCallback(async () => {
    await downloadPNG(
      '.revenue-impact-left > .chart-container:nth-child(2)',
      'weekly_revenue_decomposition'
    );
  }, []);

  const pngDownloadModelOverview = useCallback(async () => {
    await downloadPNG('.revenue-impact .revenue-impact-right', 'takeaways_ttm');
  }, []);

  return (
    <WidgetContainer
      collapsible
      header={
        <div>
          {' '}
          How does our brand equity
          <InfoTooltip size={Size.MD}>
            Brand equity is a combination of multiple underlying brand health metrics. We use brand
            equity to evaluate the combined impact of the brand health metrics on [KPI].
          </InfoTooltip>{' '}
          impact revenue?
        </div>
      }
      subHeader={
        <>
          Attribution of revenue to brand equity based on model parameter estimates.
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            Calculated for each point in time as (level of [KPI] at that point in time) - (level of
            media investments at that point in time) x (model parameter estimates of media impact on
            brand equity) x (model parameter estimates of brand equity impact on [KPI])
          </InfoTooltip>
        </>
      }
    >
      {Object.keys(revenueImpactTakeaways).length !== 0 &&
        totalRevenueDecomposition.length !== 0 &&
        weeklyRevenueDecomposition.length !== 0 && (
          <div className='revenue-impact'>
            <div className='revenue-impact-left'>
              <ChartContainer
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[
                      excelDownloadTotalRevenueDecomposition,
                      pngDownloadTotalRevenueDecomposition
                    ]}
                  />
                }
                title='Total Revenue Decomposition'
                titleAfterDashText='Trailing Twelve Months (TTM)'
              >
                <div className='bar-chart-holdings'>
                  <div className='title-container'>
                    <div className='title-text'>
                      Total:
                      {`$${(
                        (totalRevenueDecomposition[0].MediaDrivenBrandEquity +
                          totalRevenueDecomposition[0].NonMediaDrivenBrandEquity +
                          totalRevenueDecomposition[0].OtherFactors) /
                        1000000
                      ).toFixed(0)}`}
                    </div>
                    <div className='m-text'>M</div>
                  </div>
                  <div className='top-segment' />
                  <div className='top-of-barchart-line' />
                  <ResponsiveContainer width='100%' height={60}>
                    <BarChart
                      data={totalRevenueDecomposition}
                      margin={{ right: 10 }}
                      layout='vertical'
                    >
                      <ChartXAxis type='number' hide />
                      <ChartYAxis type='category' dataKey='name' hide />
                      <Bar dataKey='MediaDrivenBrandEquity' stackId='a' fill={Brand80} />
                      <Bar dataKey='NonMediaDrivenBrandEquity' stackId='a' fill={Brand50} />
                      <Bar dataKey='OtherFactors' stackId='a' fill={Neutral300} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className='bar-chart-legend'>
                    <div className='legend'>
                      <div className='top-row'>
                        <div className='bar-one' />
                        <div className='title-text'>
                          {`$${(
                            totalRevenueDecomposition[0].MediaDrivenBrandEquity / 1000000
                          ).toFixed(2)}`}
                        </div>
                        <div className='m-text'>M</div>
                        <div>
                          (
                          {percFormat(
                            totalRevenueDecomposition[0].MediaDrivenBrandEquityPercent * 100,
                            0
                          )}
                          )
                        </div>
                      </div>
                      <div className='bottom-row'>Media-Driven Brand Equity</div>
                    </div>
                    <div className='legend'>
                      <div className='top-row'>
                        <div className='bar-two' />
                        <div className='title-text'>
                          {`$${(
                            totalRevenueDecomposition[0].NonMediaDrivenBrandEquity / 1000000
                          ).toFixed(2)}`}
                        </div>
                        <div className='m-text'>M</div>
                        <div>
                          (
                          {percFormat(
                            totalRevenueDecomposition[0].NonMediaDrivenBrandEquityPercent * 100,
                            0
                          )}
                          )
                        </div>
                      </div>
                      <div className='bottom-row'>
                        <div>Non-Media-Driven Brand Equity</div>
                        <InfoTooltip>
                          Non-media-driven Brand Equity is the set of background factors that
                          contribute to brand equity outside of paid media. For example, consumer
                          experience with the product, organic moments that drive awareness and the
                          existing stock of brand equity can all contribute to business revenue but
                          are not themselves driven by paid media in the current period.
                        </InfoTooltip>
                      </div>
                    </div>
                    <div className='legend'>
                      <div className='top-row'>
                        <div className='bar-three' />
                        <div className='title-text'>
                          {`$${(totalRevenueDecomposition[0].OtherFactors / 1000000).toFixed(2)}`}
                        </div>
                        <div className='m-text'>M</div>
                        <div>
                          ( {percFormat(totalRevenueDecomposition[0].OtherFactorsPercent * 100, 0)})
                        </div>
                      </div>
                      <div className='bottom-row'>
                        <div>Other Factors</div>
                        <InfoTooltip>
                          Other factors include controls for paid media, seasonality terms and
                          macroeconomic effects.
                        </InfoTooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </ChartContainer>
              <ChartContainer
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[
                      excelDownloadWeeklyRevenueDecomposition,
                      pngDownloadWeeklyRevenueDecomposition
                    ]}
                  />
                }
                title={
                  <>
                    <Dropdown
                      overlayPlacement={Placement.RIGHT}
                      size={Size.LG}
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={weeklyRevenueDecompositionChartDateGrouping}
                      options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                      onChange={option => setWeeklyRevenueDecompositionChartDateGrouping(option)}
                    />
                    <div>Revenue Decomposition</div>
                  </>
                }
                titleAfterDashText={`${dateToMddyy(
                  weeklyRevenueDecomposition[0].date
                )} – ${dateToMddyy(
                  weeklyRevenueDecomposition[weeklyRevenueDecomposition.length - 1].date
                )}`}
              >
                <AreaChart
                  data={weeklyRevenueDecompositionByDate}
                  xAxisDataKey='date'
                  xAxisTickFormatter={val => dateToMddyy(val)}
                  dateGrouping={weeklyRevenueDecompositionChartDateGrouping}
                  yAxisStaticWidth={150}
                  yAxisTickFormatter={val => currencyFormatter.format(val, 0)}
                  tooltipFormatter={val => {
                    if (!val) {
                      return val;
                    }
                    return val < 1000
                      ? currencyFormatter.format(val, 0)
                      : currencyFormatter.format(val, 1);
                  }}
                  tooltipProps={{
                    itemKeyShape: 'line',
                    reverseItems: true
                  }}
                  colorOverrides={{
                    'Revenue from Media-Driven Brand Equity': Brand80,
                    'Revenue from Non-Media-Driven Brand Equity': Brand40,
                    'Revenue from Other Factors': Neutral400
                  }}
                  reverseLegend
                  areas={[
                    {
                      name: 'Revenue from Other Factors',
                      dataKey: 'OtherFactors',
                      toolTip:
                        'Other factors include controls for paid media, seasonality terms and macroeconomic effects.',
                      toolTipColor: Neutral600
                    },
                    {
                      name: 'Revenue from Non-Media-Driven Brand Equity',
                      dataKey: 'NonMediaDrivenBrandEquity'
                    },
                    {
                      name: 'Revenue from Media-Driven Brand Equity',
                      dataKey: 'MediaDrivenBrandEquity'
                    }
                  ]}
                  legendTitle='Revenue From:'
                />
              </ChartContainer>
            </div>
            <div className='revenue-impact-right'>
              <ChartContainer
                title='Takeaways'
                titleAfterDashText='TTM'
                rightActions={
                  <DownloadDropdown
                    onClickOptions={[excelDownloadModelOverview, pngDownloadModelOverview]}
                    menuOptions={['XLSX', 'PNG']}
                  />
                }
              >
                <div className='take-aways-container'>
                  <div className='section-one'>
                    <div className='section-one-text-container'>
                      <div className='text-left'>
                        <div>Brand</div>
                        <div>Equity</div>
                      </div>
                      <div className='text-middle'>
                        <MdArrowForward className='icon' />
                      </div>
                      <div className='text-right'>
                        <div>Incremental</div>
                        <div className='title-top-text'>Revenue</div>
                        <div className='dollar-impact'>
                          <div className='title-top-text'>
                            ~$
                            {(
                              (totalRevenueDecomposition[0].MediaDrivenBrandEquity +
                                totalRevenueDecomposition[0].NonMediaDrivenBrandEquity) /
                              1000000
                            ).toFixed(2)}
                          </div>
                          <div className='m-text'>M</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='section-two'>
                    <div className='section-two-text-container'>
                      <div className='text-left'>
                        <div>Media-Driven</div>
                        <div>Brand Equity</div>
                      </div>
                      <div className='text-middle'>
                        <MdArrowForward className='icon' />
                      </div>
                      <div className='text-right'>
                        <div>Incremental</div>
                        <div className='title-text'>Revenue</div>
                        <div className='dollar-impact-bottom'>
                          <div className='title-text'>
                            ~$
                            {(
                              totalRevenueDecomposition[0].MediaDrivenBrandEquity / 1000000
                            ).toFixed(2)}
                          </div>
                          <div className='m-text'>M</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='section-three'>
                    <div className='section-three-text'>
                      {`Brand equity accounts for about ${revenueImpactTakeaways.BrandEquityRevenuePercent.toLocaleString(
                        'en-US',
                        {
                          maximumFractionDigits: 0
                        }
                      )}% or $${(
                        (totalRevenueDecomposition[0].MediaDrivenBrandEquity +
                          totalRevenueDecomposition[0].NonMediaDrivenBrandEquity) /
                        1000000
                      ).toFixed(2)}M of the total trailing twelve
                      months revenue after controlling for other factors.`}
                    </div>
                  </div>
                </div>
              </ChartContainer>
            </div>
          </div>
        )}
    </WidgetContainer>
  );
};
